(() => {
  if (customElements.get('keysrus-product-card')) return;
  const grids = new Map();
  const frame = () => new Promise(resolve => requestAnimationFrame(resolve));
  function registerGrid(card) {
    const grid = card.closest('.product-grid');
    if (!grid) return;
    let state = grids.get(grid);
    if (!state) {
      state = { cards: new Set(), pending: 0 };
      state.schedule = () => {
        if (state.pending) return;
        state.pending = requestAnimationFrame(() => {
          state.pending = 0;
          if (!grid.isConnected || !grid.clientWidth) return;
          const inset = parseFloat(getComputedStyle(grid.firstElementChild).marginInlineStart) || 0;
          grid.style.setProperty('--keysrus-card-inset', `${inset}px`);
          const columns = innerWidth >= 990 ? 4 : innerWidth >= 750 ? 3 : 2;
          if (grid.style.getPropertyValue('--keysrus-card-columns') !== String(columns)) {
            grid.style.setProperty('--keysrus-card-columns', columns);
          }
          const rows = new Map();
          const mobileCarousel = innerWidth < 750 && grid.classList.contains('slider--tablet');
          const orderedCards = [...grid.children].map(item => item.querySelector(':scope > keysrus-product-card')).filter(Boolean);
          for (const [index, item] of orderedCards.entries()) {
            if (!item.isConnected || !item.offsetWidth) continue;
            const top = mobileCarousel ? Math.floor(index / columns) : Math.round(item.parentElement.offsetTop);
            if (!rows.has(top)) rows.set(top, []);
            rows.get(top).push(item);
          }
          for (const row of rows.values()) {
            const heading = Math.ceil(Math.max(...row.map(item => item.querySelector('.keysrus-card__identity').getBoundingClientRect().height)));
            const price = Math.ceil(Math.max(...row.map(item => item.querySelector('.keysrus-card__price-content').getBoundingClientRect().height)));
            for (const item of row) {
              item.style.setProperty('--keysrus-heading-height', `${heading}px`);
              item.style.setProperty('--keysrus-price-height', `${price}px`);
            }
          }
          const slider = grid.closest('slider-component');
          if (slider?.sliderItems) slider.initPages();
        });
      };
      state.observer = new ResizeObserver(state.schedule);
      state.observer.observe(grid);
      grids.set(grid, state);
      document.fonts?.ready.then(state.schedule);
    }
    state.cards.add(card);
    state.observer.observe(card);
    state.schedule();
    return () => {
      state.observer.unobserve(card);
      state.cards.delete(card);
      if (!state.cards.size) {
        state.observer.disconnect();
        cancelAnimationFrame(state.pending);
        grids.delete(grid);
      } else state.schedule();
    };
  }
  class KeysrusProductCard extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.connected) return;
        this.connected = true;
        this.releaseGrid = registerGrid(this);
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.quantity = this.form.elements.quantity;
        this.submit = this.form.querySelector('[type="submit"]');
        this.label = this.submit.querySelector('[data-submit-label]');
        this.defaultLabel = this.label.innerHTML;
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.form.addEventListener('submit', event => this.add(event), options);
        this.quantity.addEventListener('input', () => this.updateStepper(), options);
        this.form.querySelectorAll('[data-step]').forEach(button => button.addEventListener('click', () => {
          if (this.busy) return;
          if (!Number.isFinite(this.quantity.valueAsNumber)) this.quantity.value = this.quantity.min;
          if (button.dataset.step === 'up') this.quantity.stepUp();
          else this.quantity.stepDown();
          this.quantity.dispatchEvent(new Event('input', { bubbles: true }));
          this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }, options));
        this.updateStepper();
      });
    }
    disconnectedCallback() {
      this.events?.abort();
      this.releaseGrid?.();
      this.connected = false;
    }
    updateStepper() {
      const value = this.quantity.valueAsNumber;
      const step = Number(this.quantity.step) || 1;
      this.form.querySelector('[data-step="down"]').disabled = Boolean(this.busy) || value <= Number(this.quantity.min);
      this.form.querySelector('[data-step="up"]').disabled = Boolean(this.busy) || (this.quantity.max !== '' && value + step > Number(this.quantity.max));
    }
    setBusyLabel(text) {
      const full = this.label.querySelector('.keysrus-card__label-full');
      const short = this.label.querySelector('.keysrus-card__label-short');
      if (!full || !short) {
        this.label.textContent = text;
        return;
      }
      // Narrow cards show the short label, so keep it short while adding.
      full.textContent = text;
      short.textContent = '…';
    }
    feedback(message, state, showCart = false) {
      this.querySelector('[data-status]').textContent = message;
      this.querySelector('.keysrus-card__feedback').dataset.state = state;
      this.querySelector('[data-cart-link]').hidden = !showCart;
    }
    async cartComponents() {
      const components = [...document.querySelectorAll('cart-drawer, cart-notification')];
      await Promise.all(components.map(async component => {
        await Promise.race([customElements.whenDefined(component.localName), new Promise(resolve => setTimeout(resolve, 1500))]);
        component.init?.();
      }));
      await frame();
      return components.filter(component => typeof component.getSectionsToRender === 'function' && typeof component.renderContents === 'function');
    }
    async add(event) {
      event.preventDefault();
      if (this.busy || !this.form.reportValidity()) return;
      this.busy = true;
      this.submit.disabled = true;
      this.submit.setAttribute('aria-busy', 'true');
      this.setBusyLabel(this.dataset.adding);
      this.feedback('', '');
      this.updateStepper();
      const data = new FormData(this.form);
      this.quantity.readOnly = true;
      let confirmed = false;
      let timer;
      try {
        const components = await this.cartComponents();
        const sectionIds = [...new Set(components.flatMap(component => component.getSectionsToRender().map(section => section.id)))].slice(0, 5);
        if (sectionIds.length) {
          data.set('sections', sectionIds.join(','));
          data.set('sections_url', location.pathname + location.search);
        }
        for (const component of components) component.setActiveElement?.(this.submit);
        const controller = new AbortController();
        timer = setTimeout(() => controller.abort(), 20000);
        const response = await fetch(window.routes?.cart_add_url || this.form.action, {
          method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data, credentials: 'same-origin', signal: controller.signal
        });
        const result = await response.json();
        if (response.status >= 500) {
          this.feedback(this.dataset.uncertain, 'error', true);
          return;
        }
        if (!response.ok || result.status) {
          this.feedback(typeof result.description === 'string' ? result.description : this.dataset.error, 'error');
          return;
        }
        confirmed = true;
        this.feedback(this.dataset.added, 'success', true);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: data.get('id'), cartData: result });
        }
        if (window.theme?.settings?.cartDrawer?.atcAction === 'page') {
          location.assign(window.routes?.cart_url || this.querySelector('[data-cart-link]').href);
          return;
        }
        for (const component of components) {
          const complete = component.getSectionsToRender().every(section => typeof result.sections?.[section.id] === 'string');
          if (!complete) continue;
          component.renderContents(result);
          component.classList.remove('is-empty');
        }
      } catch {
        if (!confirmed) this.feedback(this.dataset.uncertain, 'error', true);
      } finally {
        clearTimeout(timer);
        this.busy = false;
        this.submit.disabled = false;
        this.submit.removeAttribute('aria-busy');
        this.label.innerHTML = this.defaultLabel;
        this.quantity.readOnly = false;
        this.updateStepper();
      }
    }
  }
  customElements.define('keysrus-product-card', KeysrusProductCard);
})();
